import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Grid, Switch } from '@mui/material';
import { DynamicField, Typography } from '@totalsoft/rocket-ui';
import {
  CalendarOptionRow,
  CalendarOptionsWarning,
  ColoredCircle,
  NoCalendarOptionsContainer
} from './ScheduleTypeCellParamTemplateDetailsStyles';
import { Error } from '@mui/icons-material';
import { get, over } from '@totalsoft/change-tracking-react';

function ScheduleTypeCellParamTemplateDetailsCalendarOptions({
  dataColumns,
  lens,
  isEditable
}) {
  const [t] = useTranslation();
  const selectedScheduleCells = (lens.scheduleTypeCellParamTemplateDetails |> get)?.filter(x => x['selected']);
  const allSelectedCellsAreEqual = selectedScheduleCells.every((x, _, [z]) => dataColumns.every(a => x[a.cellParamName] === z[a.cellParamName]))

  const handleOptionValueChange = useCallback((paramName, value) => {
    if (value === undefined) return; //workaround for rocket-ui numeric textfield calling onChange for no reason
    if (!allSelectedCellsAreEqual)
      over(lens.scheduleTypeCellParamTemplateDetails, prev => {
        return prev.map(x => {
          if (x.selected) dataColumns.forEach(column => (x[column.cellParamName] = null));
          return x;
        });
      });
    over(lens.scheduleTypeCellParamTemplateDetails, prev => prev.map(x => ({ ...x, [paramName]: x.selected ? value : x[paramName] })));
  }, [allSelectedCellsAreEqual, dataColumns, lens.scheduleTypeCellParamTemplateDetails]);

  return selectedScheduleCells.length ? (
    <>
      {!allSelectedCellsAreEqual && (
        <CalendarOptionsWarning item container mt={3} px={2} py={1} xs={12} gap={1} alignItems='center'>
          <Error fontSize='small' />
          <Typography>{t('ScheduleTypeCellParamTemplate.Details.OptionsWarning')}</Typography>
        </CalendarOptionsWarning>
      )}
      <Grid item container xs={12} mt={2} columnSpacing={4} rowSpacing={1}>
        {dataColumns?.map((cellParam, idx) => (
          <Grid key={idx} item container xs={12} xl={6} gap={2} alignItems='center'>
            <Grid item container xs gap={1} alignItems='center'>
              <Grid item container xs='auto'>
                <ColoredCircle colorIndex={idx} />
              </Grid>
              <CalendarOptionRow item container xs px={2} py={1} alignItems='center'>
                <Grid item xs>
                  <Typography emphasis='bold'>{cellParam.cellParamDisplayName}</Typography>
                </Grid>
                <Grid item container xs gap={1} justifyContent='center'>
                  {cellParam.controlType === 'Checkbox' ? (
                    <FormControlLabel
                      width='100%'
                      control={
                        <Switch
                          size='small'
                          checked={Boolean(allSelectedCellsAreEqual && selectedScheduleCells[0][cellParam.cellParamName])}
                          onChange={event => handleOptionValueChange(cellParam.cellParamName, event.target.checked)}
                          disabled={!isEditable}
                        />
                      }
                    />
                  ) : (
                    <DynamicField
                      id={'scheduleTypeCellParamTemplateCalendarOption_' + cellParam.cellParamName}
                      controlType={cellParam.controlType}
                      inputProps={cellParam.inputProps}
                      isClearable={false}
                      value={allSelectedCellsAreEqual ? selectedScheduleCells[0][cellParam.cellParamName] : ''}
                      onChange={value => handleOptionValueChange(cellParam.cellParamName, value)}
                      disabled={!isEditable}
                    />
                  )}
                </Grid>
              </CalendarOptionRow>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  ) : (
    <NoCalendarOptionsContainer container mt={3} p={2}>
      <Grid item container xs={12} alignItems='center' justifyContent='center'>
        <Typography color='primary' emphasis='bold' variant='button' fontSize={16}>
          {t('ScheduleTypeCellParamTemplate.Details.Options')}
        </Typography>
      </Grid>
      <Grid item container mt={1} xs={12} alignItems='center' justifyContent='center'>
        <Typography color='textSecondary' emphasis='bold'>
          {t('ScheduleTypeCellParamTemplate.Details.PleaseSelectMonth')}
        </Typography>
      </Grid>
    </NoCalendarOptionsContainer>
  );
}

ScheduleTypeCellParamTemplateDetailsCalendarOptions.propTypes = {
  dataColumns: PropTypes.array.isRequired,
  lens: PropTypes.array.isRequired,
  isEditable: PropTypes.bool.isRequired
};

export default ScheduleTypeCellParamTemplateDetailsCalendarOptions;
